import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import React, { Fragment } from 'react';
import { classNames } from '../../utils/class-names';

export interface TimeFilter {
  /** Time filter title. */
  title: string;
  /** Time filter label. */
  label: string;
  /** Time filter value. */
  value: string;
}

export const timeFilters: TimeFilter[] = [
  { title: '24 Hrs', label: 'Last 24 hours', value: '24' },
  { title: '8 Hrs', label: 'Last 8 hours', value: '8' },
  { title: '4 Hrs', label: 'Last 4 hours', value: '4' },
  { title: '2 Hrs', label: 'Last 2 hours', value: '2' },
  { title: '1 Hr', label: 'Last hour', value: '1' },
];

export interface SortOption {
  /** Sort Option Name. */
  name: string;
  /** Sort Option value. */
  value: string;
}

export const sortOptions: SortOption[] = [
  { name: 'Most Popular', value: 'shares' },
  { name: 'Newest', value: 'time' },
];

interface FilterNavProps {
  sortOptions: SortOption[];
  activeSortOption: SortOption;
  onActiveSortChange: (sort: SortOption) => void;
  timeFilterOptions: TimeFilter[];
  activeTimeFilter: TimeFilter;
  onActiveTimeFilterChange: (time: TimeFilter) => void;
  activeSharesFilter: number;
  onActiveSharesFilterChange: (shares: number) => void;
  onFilterOptionsChange: (sort: SortOption, time: TimeFilter, shares: number) => void;
}

export default function FilterNav({
  sortOptions,
  activeSortOption,
  onActiveSortChange,
  timeFilterOptions,
  activeTimeFilter,
  onActiveTimeFilterChange,
  activeSharesFilter,
  onActiveSharesFilterChange,
  onFilterOptionsChange,
}: FilterNavProps) {
  const handleSortOptionChange = (newSortOption: SortOption) => {
    if (newSortOption !== activeSortOption) {
      onFilterOptionsChange(newSortOption, activeTimeFilter, activeSharesFilter);
    }

    onActiveSortChange(newSortOption);
  };

  const handleTimeFilterSelect = (newTimeFilter: TimeFilter) => {
    if (newTimeFilter !== activeTimeFilter) {
      onFilterOptionsChange(activeSortOption, newTimeFilter, activeSharesFilter);
    }

    onActiveTimeFilterChange(newTimeFilter);
  };

  const handleSharesFilterSelect = (newSharesFilter: number) => {
    if (newSharesFilter !== activeSharesFilter) {
      onFilterOptionsChange(activeSortOption, activeTimeFilter, newSharesFilter);
    }

    onActiveSharesFilterChange(newSharesFilter);
  };

  return (
    <div className="border-b border-gray-200 bg-gray-50 py-1.5 text-sm font-medium text-gray-500">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
        {/* Sort By Options */}
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <MenuButton className="group inline-flex justify-center text-sm font-bold text-gray-700 hover:text-gray-900">
              {activeSortOption.name}
              <ChevronDownIcon
                className="shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="origin-top-left absolute left-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black/5 focus:outline-hidden">
              <div className="py-1">
                {sortOptions.map(option => (
                  <MenuItem key={option.value}>
                    {({ focus }) => (
                      <button
                        type="button"
                        className={classNames(
                          option.value === activeSortOption.value
                            ? 'font-bold text-gray-900'
                            : 'text-gray-500',
                          focus ? 'bg-gray-100' : '',
                          'block w-full text-left px-4 py-2 text-sm'
                        )}
                        onClick={() => handleSortOptionChange(option)}
                      >
                        {option.name}
                      </button>
                    )}
                  </MenuItem>
                ))}
              </div>
            </MenuItems>
          </Transition>
        </Menu>

        {/* Filter By Options (Based on Sort Selection) */}
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <MenuButton className="group inline-flex justify-center text-sm font-semibold text-gray-700 hover:text-gray-900">
              {activeSortOption.value === 'shares'
                ? activeTimeFilter.title
                : activeSharesFilter > 0
                ? `${activeSharesFilter}+`
                : 'All'}
              <ChevronDownIcon
                className="shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black/5 focus:outline-hidden">
              <div className="py-1">
                {activeSortOption.value === 'shares' &&
                  timeFilterOptions.map(option => (
                    <MenuItem key={option.label}>
                      {({ focus }) => (
                        <button
                          type="button"
                          className={classNames(
                            option.value === activeTimeFilter.value
                              ? 'font-bold text-gray-900'
                              : 'text-gray-500',
                            focus ? 'bg-gray-100' : '',
                            'block w-full text-left px-4 py-2 text-sm'
                          )}
                          onClick={() => handleTimeFilterSelect(option)}
                        >
                          {option.label}
                        </button>
                      )}
                    </MenuItem>
                  ))}

                {activeSortOption.value === 'time' &&
                  [0, 2, 3, 4, 5].map(numShare => (
                    <MenuItem key={numShare}>
                      {({ focus }) => (
                        <button
                          type="button"
                          className={classNames(
                            numShare === activeSharesFilter
                              ? 'font-bold text-gray-900'
                              : 'text-gray-500',
                            focus ? 'bg-gray-100' : '',
                            'block w-full text-left px-4 py-2 text-sm'
                          )}
                          onClick={() => handleSharesFilterSelect(numShare)}
                        >
                          {numShare > 0 ? `${numShare}+ shares` : 'All'}
                        </button>
                      )}
                    </MenuItem>
                  ))}
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}
